import React, { useMemo, useState } from "react";
import { Eye, EyeOff, KeyRound, Library, Radio, ShoppingBag, Users } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useAuth } from "../context/AuthContext";
import YasharalLivePublicFeatures from "../components/YasharalLivePublicFeatures";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function AccountLogin() {
  const location = useLocation();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const next = params.get("next") || (location.pathname.startsWith("/yasharal-live") ? "/yasharal-live" : "/portal");
  const serviceTarget = location.pathname.startsWith("/yasharal-live") || next.startsWith("/yasharal-live");
  const returnTo = params.get("return_to");
  const safeLiveReturn = (() => {
    if (!serviceTarget || !returnTo) return null;
    try {
      const url = new URL(returnTo);
      if (url.protocol !== "https:" || !["live.yasharal.vip", "yasharal-live-frontend-production.up.railway.app"].includes(url.hostname)) return null;
      return url.origin + "/yasharal-live";
    } catch { return null; }
  })();

  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    if (!identifier.trim() || !password) return;
    setBusy(true);
    try {
      const endpoint = serviceTarget ? "/yasharal-live/auth/login" : "/nationals/login";
      await axios.post(API + endpoint, {
        identifier: identifier.trim(),
        password,
      });
      const user = await refresh();
      toast.success("Shalum, " + (user?.display_name || "welcome back") + ".");
      if (safeLiveReturn) {
        window.location.assign(safeLiveReturn);
      } else {
        navigate(next.startsWith("/") ? next : "/portal", { replace: true });
      }
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not sign in");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={serviceTarget ? "min-h-screen bg-[#070a10] text-white" : "min-h-screen bg-parchment text-slate-900"}>
      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 sm:py-12">
        <div className={"grid gap-8 " + (serviceTarget ? "xl:grid-cols-[420px_1fr] xl:items-start" : "lg:grid-cols-[440px_1fr] lg:items-center")}>
          <div>
            <div className="mb-6">
              <div className={"inline-flex h-12 w-12 items-center justify-center rounded-xl " + (serviceTarget ? "bg-[#183c73]" : "bg-royal-blue")}>
                <KeyRound className={serviceTarget ? "h-6 w-6 text-[#9dc1ff]" : "h-6 w-6 text-kingdom-gold"} />
              </div>
              <h1 className={"mt-4 font-heading text-3xl " + (serviceTarget ? "text-white" : "text-royal-blue")}>One account. One sign in.</h1>
              <p className={"mt-2 leading-relaxed " + (serviceTarget ? "text-slate-400" : "text-slate-600")}>
                Use the same email or username and password across Nation access, subscriptions, Yashar’al Live, Records, Publishing and your Scripture Library.
              </p>
            </div>

            <form onSubmit={submit} className={"space-y-5 rounded-2xl border p-5 shadow-xl sm:p-6 " + (serviceTarget ? "border-slate-700 bg-[#111318]" : "border-slate-200 bg-white")}>
              <div>
                <label className={"text-sm font-semibold " + (serviceTarget ? "text-slate-300" : "text-slate-700")}>Email or username</label>
                <Input
                  autoComplete="username"
                  value={identifier}
                  onChange={(event) => setIdentifier(event.target.value)}
                  className={serviceTarget ? "mt-2 border-slate-700 bg-[#0b0d11] text-white" : "mt-2"}
                />
              </div>

              <div>
                <label className={"text-sm font-semibold " + (serviceTarget ? "text-slate-300" : "text-slate-700")}>Password</label>
                <div className="relative mt-2">
                  <Input
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    className={(serviceTarget ? "border-slate-700 bg-[#0b0d11] text-white " : "") + "pr-11"}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((value) => !value)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <Button disabled={busy || !identifier.trim() || !password} className={serviceTarget ? "h-11 w-full bg-[#8bb6ff] text-slate-950" : "btn-gold h-11 w-full"}>
                {busy ? "Signing in…" : "Sign in"}
              </Button>

              <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
                <Link to="/forgot-password" className={serviceTarget ? "text-[#9dc1ff] hover:underline" : "text-royal-blue hover:underline"}>Forgot password?</Link>
                <Link to="/" className={serviceTarget ? "text-slate-400 hover:text-white" : "text-slate-500 hover:text-royal-blue"}>Return to gov site</Link>
              </div>
            </form>

            <div className={"mt-5 text-sm " + (serviceTarget ? "text-slate-400" : "text-slate-600")}>
              Need an account?{" "}
              {serviceTarget ? (
                <Link to="/yasharal-live/signup" className="font-semibold text-[#9dc1ff] underline">Create a Yashar’al Live account</Link>
              ) : (
                <span className="inline-flex flex-wrap gap-x-3 gap-y-2">
                  <Link to="/signup" className="font-semibold text-royal-blue underline">Create free account</Link>
                  <Link to="/subscribers/signup" className="font-semibold text-royal-blue underline">Subscribe</Link>
                  <Link to="/yasharal-live/signup" className="font-semibold text-royal-blue underline">Yashar’al Live</Link>
                  <Link to="/become-national" className="font-semibold text-royal-blue underline">Become YaSHaRUN</Link>
                </span>
              )}
            </div>
          </div>

          {serviceTarget ? (
            <div className="min-w-0">
              <YasharalLivePublicFeatures />
            </div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              {[
                ["Nation", Users, "Nation membership, ministries, and portal access."],
                ["Subscriptions", ShoppingBag, "Subscriptions, purchased downloads, and billing."],
                ["Yashar’al Live", Radio, "Meet, record, produce, and broadcast."],
                ["My Library", Library, "Saved Scriptures, devotionals, palal, videos, and Turah commands."],
                ["Record Label", ShoppingBag, "Artist releases, masters and label agreements when assigned."],
                ["Publishing", Library, "Songwriter compositions, splits and publishing agreements when assigned."],
              ].map(([label, Icon, description]) => (
                <div key={label} className="border border-slate-200 bg-white p-5 shadow-sm">
                  <Icon className="h-6 w-6 text-kingdom-gold" />
                  <h2 className="mt-3 font-semibold text-royal-blue">{label}</h2>
                  <p className="mt-1 text-sm leading-relaxed text-slate-600">{description}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
